import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import axios from 'axios';
import { logError } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `$${Math.round(Number(n||0)).toLocaleString('en-US')}`;

export default function TaxSnapshotWidget({ year }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = year ? `?year=${year}` : '';
    axios.get(`${API}/tax-snapshot${params}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => logError(e, { context: 'TaxSnapshotWidget' }));
  }, [year]);

  if (!data) return <div className="animate-pulse h-32 bg-gray-100 rounded-xl" />;

  const urgency = data.balance_due > 5000 ? 'red' : data.balance_due > 1000 ? 'amber' : 'green';
  const urgencyStyle = {
    red:   'bg-red-50 border-red-200',
    amber: 'bg-amber-50 border-amber-200',
    green: 'bg-green-50 border-green-200',
  }[urgency];

  return (
    <Card className={`border ${urgencyStyle}`} data-testid="tax-snapshot-widget">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center justify-between">
          <span>{t('dashboard.tax_snapshot', 'Tax Snapshot')} — {data.year}</span>
          <Badge variant="outline" className="text-xs">
            {data.filing_status?.replace(/_/g,' ')}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: t('dashboard.gross_income','Gross Income'),        value: fmt(data.gross_income) },
            { label: t('dashboard.se_tax','Self-Employment Tax'),       value: fmt(data.se_tax) },
            { label: t('dashboard.income_tax','Federal Income Tax'),    value: fmt(data.federal_income_tax) },
            { label: t('dashboard.balance_due','Est. Balance Due'),     value: fmt(data.balance_due),
              bold: true, color: urgency === 'red' ? 'text-red-600' : urgency === 'amber' ? 'text-amber-600' : 'text-green-600' },
          ].map(({label, value, bold, color}) => (
            <div key={label}>
              <p className="text-xs text-muted-foreground">{label}</p>
              <p className={`text-xl mt-0.5 ${bold ? `font-black ${color}` : 'font-bold text-gray-900'}`}>{value}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-3 border-t flex flex-wrap gap-4 text-sm text-gray-600">
          <span>{t('dashboard.qbi','QBI deduction')}: <strong>{fmt(data.qbi_deduction)}</strong></span>
          <span>{t('dashboard.quarterly_recommended','Quarterly recommended')}: <strong>{fmt(data.quarterly_recommended)}</strong></span>
          {data.w2_withholding_credit > 0 && (
            <span>{t('dashboard.w2_withholding','W-2 withholding credit')}: <strong>({fmt(data.w2_withholding_credit)})</strong></span>
          )}
        </div>

        <p className="text-xs text-muted-foreground mt-2">{data.disclaimer}</p>
      </CardContent>
    </Card>
  );
}
